/**
 * Send a message's files again, when the message itself already arrived.
 *
 * THE GAP THIS CLOSES (2026-09-11). When an upload produced no durable
 * locator, `sanitizeGroupTurnAttachments` dropped the record and the shared
 * human doc was written text-only. Nothing ever re-posted it: the two heal
 * paths — the in-session retry in `useGlobalChat` and
 * `attachmentHealOnHydrate` — both patch the SENDER's own store and never
 * touch `group_thread_messages`. So the sender's own view healed and looked
 * fine, and every other participant had text forever. That asymmetry is
 * exactly what "it works for me" means, and it is why this went unnoticed
 * through two rounds of attachment fixes.
 *
 * Composed from machinery that already exists, deliberately: the same upload
 * function the send path uses, and `postGroupThreadMessage` keyed by the same
 * `sharedMessageId`, whose server writer merges by that id. No new endpoint,
 * no new authorization surface.
 *
 * IN-SESSION ONLY, and that is coherent rather than a limitation: the retry
 * needs the original `File` objects, which live on the message in memory, and
 * the ledger that surfaces the offer is in-memory too — a reload clears both
 * together, so the button can never appear without the bytes behind it.
 *
 * Pure of I/O by injection so the decision is unit-tested without a network.
 */
import type { SerializedAttachment } from '../../components/GlobalChatSystem/types';
import type { MessageReplyRef } from '../../types/messageReply';

export interface RetryAttachmentsDeps {
  /** Upload these files and return their records (the send path's own function). */
  uploadFiles: (files: File[], uid: string, messageId: string) => Promise<SerializedAttachment[]>;
  /** Re-post the message to the shared store with the durable records. */
  postToThread: (
    threadId: string,
    text: string,
    sharedMessageId: string,
    attachments: Array<{ name: string; type?: string; size?: number; storageUrl: string; storagePath?: string }>,
    /** Reply to a specific message (2026-09-15): the message's own `replyTo`, so a retried post keeps its quote. */
    replyTo?: MessageReplyRef,
  ) => Promise<boolean>;
}

export interface RetryAttachmentsInput {
  threadId: string;
  sharedMessageId: string;
  text: string;
  files: File[];
  uid: string;
  /** The message's `replyTo`, re-posted with it (2026-09-15). */
  replyTo?: MessageReplyRef;
}

export type RetryAttachmentsOutcome =
  /** Every file is now in the shared store. */
  | { kind: 'delivered'; records: SerializedAttachment[] }
  /** Some got through; `missing` did not. The offer stays, with a smaller count. */
  | { kind: 'partial'; missing: number; records: SerializedAttachment[] }
  /** Nothing changed — the upload failed again, or the post was refused. */
  | { kind: 'failed'; reason: string };

/** An https record the shared store will actually keep. */
function deliverable(att: SerializedAttachment | undefined | null) {
  const url = typeof att?.storageUrl === 'string' ? att.storageUrl : '';
  if (!att?.name || !url.startsWith('https://')) return null;
  return {
    name: att.name,
    type: att.type,
    size: att.size,
    storageUrl: url,
    storagePath: typeof att.storagePath === 'string' ? att.storagePath : undefined,
  };
}

export async function retryMessageAttachments(
  input: RetryAttachmentsInput,
  deps: RetryAttachmentsDeps,
): Promise<RetryAttachmentsOutcome> {
  const { threadId, sharedMessageId, text, files, uid, replyTo } = input;
  if (!threadId || !sharedMessageId || !uid || !Array.isArray(files) || files.length === 0) {
    return { kind: 'failed', reason: 'nothing-to-retry' };
  }

  let records: SerializedAttachment[];
  try {
    records = await deps.uploadFiles(files, uid, sharedMessageId);
  } catch {
    return { kind: 'failed', reason: 'upload-failed' };
  }

  const ready = records.map(deliverable).filter((x): x is NonNullable<typeof x> => x !== null);
  if (ready.length === 0) return { kind: 'failed', reason: 'upload-failed' };

  let posted = false;
  try {
    // The fifth argument only when there is a quote to keep — callers (and
    // their pins) that never heard of replies see exactly the call they did.
    posted = replyTo
      ? await deps.postToThread(threadId, text, sharedMessageId, ready, replyTo)
      : await deps.postToThread(threadId, text, sharedMessageId, ready);
  } catch {
    return { kind: 'failed', reason: 'post-refused' };
  }
  if (!posted) return { kind: 'failed', reason: 'post-refused' };

  const missing = files.length - ready.length;
  return missing > 0
    ? { kind: 'partial', missing, records }
    : { kind: 'delivered', records };
}
